'use client';

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import Image from 'next/image';
import { SettingsDocumentData } from 'prismicio-types';

import { MMID_IMAGE, MMID_MESSAGE } from '@/constants';
import { useIsMobile } from '@/hooks';
import { IconSvg, useData } from '@/components';
import styles from './MMIDInfo.module.scss';
import { AnchorProps } from '@headlessui/react/dist/internal/floating';

const CLOSE_DELAY = 200;

type MMIDInfoAnchorPlacement = 'default' | 'bottom';

const getAnchorConfig = (
  anchorPlacement: MMIDInfoAnchorPlacement,
  isMobile: boolean,
) => {
  const to = anchorPlacement === 'bottom' || isMobile ? 'bottom end' : 'right';

  return { to, gap: '4px' };
};

const MMIDInfo: FC<MMIDInfoProps> = ({ anchorPlacement = 'default' }) => {
  const { settings } = useData();
  const { isMobile } = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getMMIDCardData = (settings: SettingsDocumentData): MMIDCardData => {
    return {
      title: settings?.mmid_description || MMID_MESSAGE,
      imageUrl: settings?.mmid_card?.url || MMID_IMAGE,
    };
  };

  const mmiCard = useMemo(() => getMMIDCardData(settings), [settings]);
  const anchorConfig = useMemo(
    () => getAnchorConfig(anchorPlacement, isMobile),
    [anchorPlacement, isMobile],
  );

  const clearCloseTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const openPopover = useCallback(() => {
    clearCloseTimeout();
    setIsOpen(true);
  }, [clearCloseTimeout]);

  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, CLOSE_DELAY);
  }, [clearCloseTimeout]);

  const handleMouseEnter = openPopover;
  const handleFocus = openPopover;

  const handleMouseLeave = scheduleClose;
  const handleBlur = scheduleClose;

  useEffect(() => {
    return () => clearCloseTimeout();
  }, [clearCloseTimeout]);
  return (
    <Popover className={styles.store__status_info__help}>
      <PopoverButton
        aria-label="Medical Marijuana Identification Card information"
        onBlur={handleBlur}
        onFocus={handleFocus}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <IconSvg
          decorative={false}
          name="alert-info"
          title="MMID information"
        />
      </PopoverButton>

      {isOpen && (
        <PopoverPanel
          anchor={anchorConfig as AnchorProps}
          className={styles.store__status_info__help_body}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          static
          style={{
            zIndex: 9999,
            maxWidth: '320px',
            ...(isMobile
              ? { left: '15px', right: 'auto', transform: 'translateX(0px)' }
              : {}),
          }}
        >
          <div className={styles.info__help_body}>
            <div className={styles.popover_body}>
              {mmiCard.title && (
                <p className={styles.popover_body__description}>
                  {mmiCard.title}
                </p>
              )}
              {mmiCard.imageUrl && (
                <div className={styles.popover_body__image_wrapper}>
                  <Image
                    alt={mmiCard.title || 'Medical Marijuana ID Card'}
                    className={styles.popover_body__image}
                    height={200}
                    src={mmiCard.imageUrl}
                    width={350}
                  />
                </div>
              )}
            </div>
          </div>
        </PopoverPanel>
      )}
    </Popover>
  );
};

interface MMIDCardData {
  title: string;
  imageUrl: string;
}

interface MMIDInfoProps {
  anchorPlacement?: MMIDInfoAnchorPlacement;
}

export { MMIDInfo };
